import styled from '@emotion/styled';
import Image from 'next/image';
import { InnerContent, InnerTitle, TextWrapper } from '../../comm';
import monitoLogo from './monito-logo.svg';
import monito from './monito.svg';
import TitleWithDivider from '@/components/comm/TitleWithDivider';
import { breakpointMedium, breakpointSmall } from '@/constants/breakpoints';
import { colors } from '@/constants/colors';
import { CS_URL } from '@/constants/urls';
import checkIcon from '@/public/images/ic-check.svg';
import clockImg from '@/public/images/img-clock-warn.svg';

const CardContainer = styled.div`
display: grid;
column-gap: 32px;
grid-template-columns: 1fr 1fr;

@media (max-width: ${breakpointSmall}) {
  grid-template-columns: 1fr;
  row-gap: 20px;
}
`;

const Card = styled.div`
  display: grid;
  border: 1px solid ${colors.lightGrey2};
  border-radius: 15px;
`;

const CardTextWrapper = styled.div`
display: grid;
padding: 18px 36px 36px 36px;
word-break: keep-all;

@media (max-width: ${breakpointMedium}) {
  padding: 12px 20px 20px 20px;
  font-size: 15px;
}
`;

const CardTitle = styled.span`
padding-bottom: 5px;
font-size: 18px;
font-weight: 700;

@media (max-width: ${breakpointMedium}) {
  font-size: 15px;
}
`;

const CardDesc = styled.span`
color: ${colors.darkGrey2};
font-size: 16px;

@media (max-width: ${breakpointMedium}) {
  font-size: 13px;
}
`;

const StyledA = styled.a`
  color: ${colors.blue};
  text-decoration: underline;
`;

const RuleContainer = styled.div`
display: grid;
padding: 20px;
margin-top: 100px;
background-color: ${colors.brightBlue};
grid-auto-flow: column;
grid-template-columns: 130px auto;
grid-template-rows: auto repeat(4, 1fr);

@media (max-width: ${breakpointMedium}) {
  padding: 20px;
  margin-top: 60px;
}

@media (max-width: ${breakpointSmall}) {
  grid-template-columns: 1fr;
  grid-template-rows: 100px auto repeat(4, 1fr);
}
`;

const RuleTitle = styled.span`
padding: 10px 0;
font-size: 18px;
font-weight: 700;

@media (max-width: ${breakpointMedium}) {
  font-size: 16px;
}

@media (max-width: ${breakpointSmall}) {
  text-align: center;
}
`;

const RuleText = styled.span`
padding: 2px 0;
font-size: 15px;
word-break: keep-all;

@media (max-width: ${breakpointMedium}) {
  font-size: 13px;
}
`;

const IconWrapper = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 6px;
`;

const ClockImg = styled.div`
align-self: center;
grid-row: 1 / 6;

@media (max-width: ${breakpointSmall}) {
  grid-row: 1 / 2;
  justify-self: center;
}
`;

const CheckIcon = styled.div`
width: 24px;
height: 24px;
flex-shrink: 0;

@media (max-width: ${breakpointMedium}) {
  width: 20px;
  height: 20px;
}
`;

export const PCCPProgress = () => (
  <TitleWithDivider title="시험 진행 안내">
    <TextWrapper gap="5px">
      <InnerTitle>비대면 시험 진행</InnerTitle>
      <InnerContent>프로그래머스 코딩전문역량인증(PCCP)는 안정적인 온라인 IBT(Internet Based Test) 솔루션 <StyledA href="https://monito.io/" target="_blank">모니토</StyledA>로 진행합니다.</InnerContent>
    </TextWrapper>
    <CardContainer>
      <Card>
        <div>
          <Image
            src={monitoLogo}
            alt="monito logo"
            style={{ maxWidth: '100%', height: 'auto' }}
          />
        </div>
        <CardTextWrapper>
          <CardTitle>1만명 동시 접속 시험에도 안정적인 테스트 가능</CardTitle>
          <CardDesc>별도 설치나 추가 구매 없이 시험 접수만으로 검증된 기술력을 가진 모니토 솔루션으로 응시할 수 있습니다.</CardDesc>
        </CardTextWrapper>
      </Card>
      <Card>
        <div>
          <Image
            src={monito}
            alt="monito"
            style={{ maxWidth: '100%', height: 'auto' }}
          />
        </div>
        <CardTextWrapper>
          <CardTitle>화상 감독 기능으로 공정한 시험 환경 제공</CardTitle>
          <CardDesc>웹캠과 스마트폰으로 응시자의 비정상적인 시선∙행동∙소리 패턴을 분석하여 이상행위를 감지합니다.</CardDesc>
        </CardTextWrapper>
      </Card>
    </CardContainer>
    <RuleContainer>
      <ClockImg><Image src={clockImg} alt="clock" /></ClockImg>
      <RuleTitle>시험 시간 규정</RuleTitle>
      <IconWrapper>
        <CheckIcon><Image src={checkIcon} alt="check" /></CheckIcon>
        <RuleText>시험시작 시간 1시간 전부터 입실 가능합니다.</RuleText>
      </IconWrapper>
      <IconWrapper>
        <CheckIcon><Image src={checkIcon} alt="check" /></CheckIcon>
        <RuleText>시험시작 시간 20분 전까지만 시험 환경에 입실할 수 있습니다.</RuleText>
      </IconWrapper>
      <IconWrapper>
        <CheckIcon><Image src={checkIcon} alt="check" /></CheckIcon>
        <RuleText>입실 시간 이후에는 입장할 수 없으며 시험에 응시하실 수 없습니다.</RuleText>
      </IconWrapper>
      <IconWrapper>
        <CheckIcon><Image src={checkIcon} alt="check" /></CheckIcon>
        <RuleText>예상치 못한 시스템 문제로 접속이 안되는 경우, 홈페이지 <StyledA target="_blank" href={CS_URL}>1:1 채팅상담</StyledA>을 통해 문의하시기 바랍니다.</RuleText>
      </IconWrapper>
    </RuleContainer>
  </TitleWithDivider>
);

export const PCCEProgress = () => (
  <TitleWithDivider title="시험 진행 안내">
    <TextWrapper gap="5px">
      <InnerTitle>비대면 시험 진행</InnerTitle>
      <InnerContent>프로그래머스 코딩필수역량인증(PCCE)은 안정적인 온라인 IBT(Internet Based Test) 솔루션 <StyledA href="https://monito.io/" target="_blank">모니토</StyledA>로 진행합니다.</InnerContent>
    </TextWrapper>
    <CardContainer>
      <Card>
        <div>
          <Image
            src={monitoLogo}
            alt="monito logo"
            style={{ maxWidth: '100%', height: 'auto' }}
          />
        </div>
        <CardTextWrapper>
          <CardTitle>1만명 동시 접속 시험에도 안정적인 테스트 가능</CardTitle>
          <CardDesc>별도 설치나 추가 구매 없이 시험 접수만으로 검증된 기술력을 가진 모니토 솔루션으로 응시할 수 있습니다.</CardDesc>
        </CardTextWrapper>
      </Card>
      <Card>
        <div>
          <Image
            src={monito}
            alt="monito"
            style={{ maxWidth: '100%', height: 'auto' }}
          />
        </div>
        <CardTextWrapper>
          <CardTitle>화상 감독 기능으로 공정한 시험 환경 제공</CardTitle>
          <CardDesc>웹캠과 스마트폰으로 응시자의 비정상적인 시선∙행동∙소리 패턴을 분석하여 이상행위를 감지합니다.</CardDesc>
        </CardTextWrapper>
      </Card>
    </CardContainer>
    <RuleContainer>
      <ClockImg><Image src={clockImg} alt="clock" /></ClockImg>
      <RuleTitle>시험 시간 규정</RuleTitle>
      <IconWrapper>
        <CheckIcon><Image src={checkIcon} alt="check" /></CheckIcon>
        <RuleText>시험시작 시간 1시간 전부터 입실 가능합니다.</RuleText>
      </IconWrapper>
      <IconWrapper>
        <CheckIcon><Image src={checkIcon} alt="check" /></CheckIcon>
        <RuleText>시험시작 시간 20분 전까지만 시험 환경에 입실할 수 있습니다.</RuleText>
      </IconWrapper>
      <IconWrapper>
        <CheckIcon><Image src={checkIcon} alt="check" /></CheckIcon>
        <RuleText>입실 시간 이후에는 입장할 수 없으며 시험에 응시하실 수 없습니다.</RuleText>
      </IconWrapper>
      <IconWrapper>
        <CheckIcon><Image src={checkIcon} alt="check" /></CheckIcon>
        <RuleText>예상치 못한 시스템 문제로 접속이 안되는 경우, 홈페이지 <StyledA target="_blank" href={CS_URL}>1:1 채팅상담</StyledA>을 통해 문의하시기 바랍니다.</RuleText>
      </IconWrapper>
    </RuleContainer>
  </TitleWithDivider>
);

export const PCSQLProgress = () => (
  <TitleWithDivider title="시험 진행 안내">
    <TextWrapper gap="5px">
      <InnerTitle>비대면 시험 진행</InnerTitle>
      <InnerContent>프로그래머스 SQL역량인증(PCSQL)은 안정적인 온라인 IBT(Internet Based Test) 솔루션 <StyledA href="https://monito.io/" target="_blank">모니토</StyledA>로 진행합니다.</InnerContent>
    </TextWrapper>
    <CardContainer>
      <Card>
        <div>
          <Image
            src={monitoLogo}
            alt="monito logo"
            style={{ maxWidth: '100%', height: 'auto' }}
          />
        </div>
        <CardTextWrapper>
          <CardTitle>1만명 동시 접속 시험에도 안정적인 테스트 가능</CardTitle>
          <CardDesc>별도 설치나 추가 구매 없이 시험 접수만으로 검증된 기술력을 가진 모니토 솔루션으로 응시할 수 있습니다.</CardDesc>
        </CardTextWrapper>
      </Card>
      <Card>
        <div>
          <Image
            src={monito}
            alt="monito"
            style={{ maxWidth: '100%', height: 'auto' }}
          />
        </div>
        <CardTextWrapper>
          <CardTitle>화상 감독 기능으로 공정한 시험 환경 제공</CardTitle>
          <CardDesc>웹캠과 스마트폰으로 응시자의 비정상적인 시선∙행동∙소리 패턴을 분석하여 이상행위를 감지합니다.</CardDesc>
        </CardTextWrapper>
      </Card>
    </CardContainer>
    <RuleContainer>
      <ClockImg><Image src={clockImg} alt="clock" /></ClockImg>
      <RuleTitle>시험 시간 규정</RuleTitle>
      <IconWrapper>
        <CheckIcon><Image src={checkIcon} alt="check" /></CheckIcon>
        <RuleText>시험시작 시간 1시간 전부터 입실 가능합니다.</RuleText>
      </IconWrapper>
      <IconWrapper>
        <CheckIcon><Image src={checkIcon} alt="check" /></CheckIcon>
        <RuleText>시험시작 시간 20분 전까지만 시험 환경에 입실할 수 있습니다.</RuleText>
      </IconWrapper>
      <IconWrapper>
        <CheckIcon><Image src={checkIcon} alt="check" /></CheckIcon>
        <RuleText>입실 시간 이후에는 입장할 수 없으며 시험에 응시하실 수 없습니다.</RuleText>
      </IconWrapper>
      <IconWrapper>
        <CheckIcon><Image src={checkIcon} alt="check" /></CheckIcon>
        <RuleText>예상치 못한 시스템 문제로 접속이 안되는 경우, 홈페이지 <StyledA target="_blank" href={CS_URL}>1:1 채팅상담</StyledA>을 통해 문의하시기 바랍니다.</RuleText>
      </IconWrapper>
    </RuleContainer>
  </TitleWithDivider>
);
